import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, GraduationCap, MapPin } from 'lucide-react'
import { analyticsService } from '@meiuca/analytics'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { MarketplaceFooter } from '@/components/MarketplaceFooter'
import { trainingCenterService } from '@/services/trainingCenterService'
import {
  formatMinutes,
  getLevelLabel,
  getModalityLabel,
  getLevelTagClass,
  getWeekdayLabel,
  getWeekdayShortLabel,
  type TrainingCenterDetail,
  type TrainingCenterScheduleClass
} from '@/types/trainingCenter'

/** Dias na ordem em que o backend numera (1 = domingo). */
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

/**
 * Grade semanal de turmas do centro, numa tela própria ("Ver turmas" da
 * vitrine). É informativa — turma é template semanal, sem data; marcar aula
 * acontece na agenda datada ("Marcar aula").
 */
export function TrainingCenterClassesPage() {
  const { centerId } = useParams<{ centerId: string }>()
  const navigate = useNavigate()

  const [center, setCenter] = useState<TrainingCenterDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [onlyWithSpots, setOnlyWithSpots] = useState(false)
  const [selectedWeekday, setSelectedWeekday] = useState<number | null>(null)
  const [locationFilter, setLocationFilter] = useState<string | null>(null)

  useEffect(() => {
    analyticsService.trackScreenView('ct_classes')
  }, [])

  const loadCenter = useCallback(async () => {
    if (!centerId) return

    try {
      setLoading(true)
      setNotFound(false)
      setCenter(await trainingCenterService.getById(centerId))
    } catch (err) {
      console.error('[TrainingCenterClassesPage] Error loading center:', err)
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [centerId])

  useEffect(() => {
    void loadCenter()
  }, [loadCenter])

  const locationsById = useMemo(
    () => new Map((center?.locations ?? []).map((location) => [location.id, location])),
    [center]
  )

  // O filtro só faz sentido com mais de um local. O nome, porém, aparece em
  // toda turma mesmo assim: cada linha tem que ser lida sozinha, sem depender
  // do que está selecionado no filtro.
  const hasManyLocations = (center?.locations.length ?? 0) > 1

  /**
   * A semana já filtrada, uma entrada por dia que tem turma.
   *
   * Turma sem limite conta como disponível: ilimitado é o oposto de cheio.
   */
  const week = useMemo(() => {
    const matches = (item: TrainingCenterScheduleClass) => {
      const available =
        item.availableSpots === null || item.availableSpots === undefined || item.availableSpots > 0
      if (onlyWithSpots && !available) return false
      if (locationFilter && item.locationId !== locationFilter) return false
      return true
    }

    const grouped = new Map<number, TrainingCenterScheduleClass[]>()
    ;(center?.weeklySchedule ?? []).filter(matches).forEach((item) => {
      grouped.set(item.weekday, [...(grouped.get(item.weekday) ?? []), item])
    })

    return WEEKDAYS.map((weekday) => ({
      weekday,
      classes: (grouped.get(weekday) ?? []).sort((a, b) => a.startTimeMin - b.startTimeMin)
    })).filter((day) => day.classes.length > 0)
  }, [center, locationFilter, onlyWithSpots])

  // O dia escolhido, se os filtros ainda o deixam existir; senão o primeiro
  // com turma — abrir num dia vazio faria a agenda parecer inexistente.
  const activeDay = week.find((day) => day.weekday === selectedWeekday) ?? week[0] ?? null

  if (loading) {
    return (
      <div className="min-h-screen bg-background-secondary">
        <MarketplaceHeader onLocationClick={() => {}} />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  if (notFound || !center) {
    return (
      <div className="min-h-screen bg-background-secondary flex flex-col">
        <MarketplaceHeader onLocationClick={() => {}} />
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center py-32">
          <GraduationCap className="w-12 h-12 text-text-tertiary mb-4" />
          <p className="text-text-primary font-medium">Centro não disponível</p>
          <p className="text-text-secondary text-sm mt-1 max-w-md">
            Este centro de treinamento não está mais na vitrine do app.
          </p>
          <button
            onClick={() => navigate('/cts/search')}
            className="mt-6 px-4 py-2 rounded-lg bg-brand-primary text-white font-medium"
          >
            Ver outros centros
          </button>
        </div>
        <MarketplaceFooter />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-secondary flex flex-col">
      <MarketplaceHeader onLocationClick={() => {}} />

      <div className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/ct/${center.id}`)}
            className="rounded-full bg-background-white p-2 shadow-sm border border-border-light"
            aria-label="Voltar"
          >
            <ArrowLeft className="w-5 h-5 text-text-primary" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-text-primary">Turmas</h1>
            <p className="truncate text-sm text-text-secondary">{center.name}</p>
          </div>
        </div>

        <section className="bg-background-white rounded-lg border border-border-light p-5 space-y-4">
          {/* Filtros num bloco só: soltos pela seção, competiam entre si e com
              a própria agenda. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg bg-background-secondary px-4 py-3">
            {hasManyLocations && (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-text-secondary">Local</span>
                <select
                  value={locationFilter ?? ''}
                  onChange={(event) => {
                    const value = event.target.value || null
                    setLocationFilter(value)
                    if (value) {
                      analyticsService.track('ct_schedule_location_selected', { ct_id: center.id })
                    }
                  }}
                  className="rounded-lg border border-border-primary bg-background-white px-2 py-1.5 text-sm text-text-primary"
                >
                  <option value="">Todos</option>
                  {center.locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyWithSpots}
                onChange={(event) => setOnlyWithSpots(event.target.checked)}
                className="h-4 w-4 rounded border-border-primary"
              />
              <span className="text-text-secondary">Só com vaga</span>
            </label>
          </div>

          {week.length === 0 ? (
            <p className="py-6 text-center text-sm text-text-secondary">
              Nenhuma turma com os filtros escolhidos.
            </p>
          ) : (
            /* Navegação semanal, igual nos apps: a régua mostra a semana
               inteira de relance — dias sem turma ficam apagados — e a lista
               mostra um dia por vez. As tentativas anteriores falharam nos
               dois extremos: colunas lado a lado viravam vãos gigantes com
               dias desiguais e não cabiam no celular; dias empilhados faziam
               a página inteira ser rolagem. */
            <div className="space-y-3">
              <div className="flex gap-1.5">
                {WEEKDAYS.map((weekday) => {
                  const day = week.find((candidate) => candidate.weekday === weekday)
                  const isActive = activeDay?.weekday === weekday

                  return (
                    <button
                      key={weekday}
                      type="button"
                      disabled={!day}
                      onClick={() => {
                        setSelectedWeekday(weekday)
                        analyticsService.track('ct_schedule_day_selected', { weekday })
                      }}
                      className={`flex-1 rounded-lg border py-2 text-center transition-colors ${
                        isActive
                          ? 'border-brand-primary bg-brand-primary text-white'
                          : day
                            ? 'border-border-light bg-background-white text-text-primary hover:bg-background-tertiary'
                            : 'cursor-default border-transparent bg-background-secondary text-text-tertiary opacity-50'
                      }`}
                    >
                      <span className="block text-xs font-semibold sm:text-sm">
                        {getWeekdayShortLabel(weekday)}
                      </span>
                      <span
                        className={`block text-[11px] ${
                          isActive ? 'text-white/80' : 'text-text-tertiary'
                        }`}
                      >
                        {day ? day.classes.length : '–'}
                      </span>
                    </button>
                  )
                })}
              </div>

              {activeDay && (
                <div className="rounded-lg border border-border-light">
                  <div className="flex items-baseline justify-between rounded-t-lg border-b border-border-light bg-background-secondary px-4 py-2.5">
                    <p className="text-sm font-semibold text-text-primary">
                      {getWeekdayLabel(activeDay.weekday)}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      {activeDay.classes.length}{' '}
                      {activeDay.classes.length === 1 ? 'turma' : 'turmas'}
                    </p>
                  </div>

                  <div className="divide-y divide-border-light">
                    {activeDay.classes.map((item) => {
                      const location = item.locationId ? locationsById.get(item.locationId) : undefined

                      return (
                        <div key={item.id} className="space-y-1 px-4 py-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold tabular-nums text-text-primary">
                              {formatMinutes(item.startTimeMin)} – {formatMinutes(item.endTimeMin)}
                            </p>
                            <SpotsBadge availableSpots={item.availableSpots} />
                          </div>

                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            {item.modality && (
                              <span className="inline-flex items-center rounded bg-brand-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-brand-primary">
                                {getModalityLabel(item.modality)}
                              </span>
                            )}
                            {item.level && (
                              <span
                                className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${getLevelTagClass(item.level)}`}
                              >
                                {getLevelLabel(item.level)}
                              </span>
                            )}
                            {location && (
                              <span className="flex min-w-0 items-center gap-1 text-xs text-text-secondary">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{location.name}</span>
                              </span>
                            )}
                          </div>

                          {item.description && (
                            <p className="line-clamp-1 text-xs text-text-tertiary">
                              {item.description}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <MarketplaceFooter />
    </div>
  )
}

/** Turma sem limite não mostra selo: "vagas ilimitadas" não diz nada a ninguém. */
function SpotsBadge({ availableSpots }: { availableSpots?: number | null }) {
  if (availableSpots === null || availableSpots === undefined) {
    return null
  }

  if (availableSpots === 0) {
    return (
      <span className="flex-shrink-0 inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-text-secondary">
        Turma cheia
      </span>
    )
  }

  return (
    <span className="flex-shrink-0 inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-emerald-50 text-emerald-700">
      {availableSpots} {availableSpots === 1 ? 'vaga' : 'vagas'}
    </span>
  )
}
